"use client";

import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";

import { Link } from "@/i18n/navigation";
import { TournamentStatus, type Organizer, type Tournament, type PrizeType } from "@/entities/tournament/model/types";
import { useDebounce } from "@/hooks/useDebounce";
import { useInfiniteTournaments } from "@/hooks/useTournaments";
import { useUserProfile } from "@/hooks/useAuth";
import {
  isOrgAdminRole,
  isSiteAdmin as roleIsSiteAdmin,
} from "@/shared/lib/platform-roles";
import { useTournamentsWithFriends } from "@/hooks/useFriends";
import { useUserOrganizationsWithRoles } from "@/hooks/useUserOrganizations";
import { LOCAL_STORAGE_KEYS } from "@/shared/config";
import { archiveToTournament } from "@/entities/tournament/lib/archive-to-tournament";
import { getTournamentStatusKey } from "@/entities/tournament/lib/tournament-lifecycle";
import { TournamentFilters } from "@/widgets/tournaments/tournament-filters";
import { ViewMode, SortByType, type StatusFilterValue } from "@/widgets/tournaments/tournament-filters";
import { TournamentListOptimized } from "@/widgets/tournaments/tournament-list-optimized";
import { Button } from "@/shared/ui/buttons/button";
import { api } from "@/shared/lib/api";
import { useDelayedLoading } from "@/shared/lib/hooks/use-delayed-loading";
import { archiveKeys } from "@/shared/api/query-keys";


const VIEW_MODE_STORAGE_KEY = LOCAL_STORAGE_KEYS.TOURNAMENTS_VIEW_MODE;

/**
 * `serverFallback` — серверный список ссылок на турниры (KAN-1225): его видно,
 * пока клиентский список не получил первые данные. На сервере запрос React
 * Query ещё не выполнялся, и без него в HTML уходило пустое состояние.
 */
export function TournamentsPageClient({
  serverFallback,
}: {
  serverFallback?: React.ReactNode;
}) {
  const { data: me } = useUserProfile();
  const { data: orgs } = useUserOrganizationsWithRoles(me?.id ?? undefined);
  const isSiteAdmin = roleIsSiteAdmin(me);
  const t = useTranslations("Tournaments.page");
  const canCreateTournament = useMemo(
    () => Boolean((orgs ?? []).some((o) => isOrgAdminRole(String(o.role)))),
    [orgs],
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>("all");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [sortBy, setSortBy] = useState<SortByType>("startDate");
  const [withFriends, setWithFriends] = useState(false);
  const debouncedSearch = useDebounce(searchTerm, 300);
  const { data: tournamentsWithFriends = [] } = useTournamentsWithFriends(!!me);

  useEffect(() => {
    if (!canCreateTournament && statusFilter === "Draft") {
      setStatusFilter("all");
    }
  }, [canCreateTournament, statusFilter]);
  useEffect(() => {
    const savedMode = localStorage.getItem(VIEW_MODE_STORAGE_KEY) as ViewMode;
    if (savedMode) {
      setViewMode(savedMode);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
    }
  }, [viewMode]);

  const sortDir = useMemo(() => {
    if (sortBy === "startDate") return "asc";
    if (sortBy === "prizePool") return "desc";
    if (sortBy === "participants") return "desc";
    return "asc";
  }, [sortBy]);

  // Архив публичен (эндпоинт AllowAnonymous) — доступен всем через чип-фильтр «Архив».
  const isArchiveView = statusFilter === "Archive";
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteTournaments({
      ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
      ...(statusFilter !== "all" && !isArchiveView
        ? { status: String(statusFilter) }
        : {}),
      sortBy,
      sortDir,
      limit: 24,
      enabled: !isArchiveView,
    });

  const tournaments: Tournament[] = useMemo(
    () =>
      (data?.pages ?? []).flatMap((p) =>
        (p.items ?? []).map((item) => {
          const org = item.organizer;
          const organizer: Organizer | undefined = org
            ? {
                id: org.id ?? "",
                name: org.name ?? "",
                avatar: org.avatar ?? undefined,
              }
            : undefined;
          const prizeDistribution = item.prizeDistribution?.map((row) => ({
            placeFrom: row.placeFrom,
            placeTo: row.placeTo,
            prizeType: (row.prizeType ?? "Money") as PrizeType,
            sharePercent: row.sharePercent,
            quantity: row.quantity,
            description: row.description,
          }));
          return {
            ...item,
            name: item.name ?? "",
            status: (item.status ?? undefined) as TournamentStatus | undefined,
            type: item.type ?? undefined,
            rules: item.rules ?? undefined,
            organizer,
            prizeDistribution,
          };
        }),
      ),
    [data],
  );

  // KAN-670: «Все» должно значить все. Раньше архив жил только в своём режиме, и
  // два списка не пересекались: «Все» никогда не показывал завершённые, «Архив» —
  // идущие. Поэтому архив грузим и в «Все» тоже.
  const archiveNeeded = isArchiveView || statusFilter === "all";

  // Пачками по 50 (потолок эндпоинта) со смещением — раньше архив брался одной
  // пачкой в 50 записей и молча обрывался, будто больше ничего и нет.
  const ARCHIVE_PAGE_SIZE = 50;
  const {
    data: archivePages,
    isLoading: archivesLoading,
    fetchNextPage: fetchNextArchivePage,
    hasNextPage: hasNextArchivePage,
    isFetchingNextPage: isFetchingNextArchivePage,
  } = useInfiniteQuery({
    queryKey: archiveKeys.list(ARCHIVE_PAGE_SIZE),
    queryFn: async ({ pageParam }) => {
      const res = await api.get<Record<string, unknown>[]>(
        "/tournament-archives",
        {
          params: { count: ARCHIVE_PAGE_SIZE, offset: pageParam },
        },
      );
      return Array.isArray(res.data) ? res.data : [];
    },
    initialPageParam: 0,
    // Неполная страница = архив кончился. Следующее смещение считаем по уже
    // полученным записям, а не по номеру страницы: так оно не разъедется, если
    // сервер отдаст меньше запрошенного.
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < ARCHIVE_PAGE_SIZE
        ? undefined
        : allPages.reduce((sum, page) => sum + page.length, 0),
    staleTime: 30_000,
    enabled: archiveNeeded,
  });

  const archiveAsTournaments: Tournament[] = useMemo(() => {
    const rows = (archivePages?.pages ?? []).flat();
    return rows.map(archiveToTournament);
  }, [archivePages]);

  const tournamentIdsWithFriends = useMemo(
    () => new Set(tournamentsWithFriends.map((x) => x.tournamentId)),
    [tournamentsWithFriends],
  );
  const tournamentFriendsMap = useMemo(() => {
    const m = new Map<
      string,
      { friends: (typeof tournamentsWithFriends)[0]["friends"]; count: number }
    >();
    for (const x of tournamentsWithFriends) {
      m.set(x.tournamentId, { friends: x.friends, count: x.friendsCount });
    }
    return m;
  }, [tournamentsWithFriends]);

  const visibleTournaments = useMemo(() => {
    if (isArchiveView) return archiveAsTournaments;
    const isArchived = (statusKey: string) =>
      statusKey === "cancelled" || statusKey === "canceled";
    const isDraft = (statusKey: string) => statusKey === "draft";
    const matchesFilter = (t: Tournament) => {
      const key = getTournamentStatusKey(t);
      if (isArchived(key)) return false;
      // Черновики в ответе бэка — ТОЛЬКО свои (см. GetPagedAsync currentUserId),
      // поэтому в «Все» их показываем (с бейджем «Черновик»), а не режем.
      if (statusFilter === "all") return true;
      const f = String(statusFilter).toLowerCase();
      if (f === "inprogress")
        return key === "inprogress" || key === "ongoing" || key === "active";
      if (f === "upcoming") return key === "upcoming";
      if (f === "registrationopen") return key === "registrationopen";
      if (f === "registrationclosed") return key === "registrationclosed";
      return !isDraft(key);
    };
    let list = (tournaments ?? []).filter(matchesFilter);

    // KAN-670: в «Все» подмешиваем архив и сортируем всё по дате. Дедуп по id
    // обязателен: только что завершённый турнир какое-то время лежит и в живом
    // списке, и в архиве, и без этого он показался бы дважды. Живая запись
    // выигрывает — в ней больше полей (баннер, организатор, права).
    if (statusFilter === "all" && archiveAsTournaments.length > 0) {
      const seen = new Set(list.map((t) => String(t.id)));
      const extra = archiveAsTournaments.filter((t) => !seen.has(String(t.id)));
      list = [...list, ...extra].sort((a, b) => {
        const dateOf = (t: Tournament) =>
          Date.parse(String(t.endDate ?? t.startDate ?? "")) || 0;
        return dateOf(b) - dateOf(a);
      });
    }

    if (withFriends && me) {
      list = list.filter((t) => tournamentIdsWithFriends.has(String(t.id)));
    }
    return list;
  }, [
    tournaments,
    statusFilter,
    isArchiveView,
    archiveAsTournaments,
    withFriends,
    me,
    tournamentIdsWithFriends,
  ]);

  // KAN-306: delay-in + min-out — на мгновенных ответах скелетон не мелькает
  const rawListLoading = isArchiveView
    ? archivesLoading && visibleTournaments.length === 0
    : (isLoading || (archiveNeeded && archivesLoading)) &&
      visibleTournaments.length === 0;
  const showListSkeleton = useDelayedLoading(rawListLoading);
  // Данных ещё нет вовсе (сервер, первая отрисовка) — не «пусто», а «не знаем».
  const awaitingFirstData =
    visibleTournaments.length === 0 &&
    (isArchiveView
      ? archivePages === undefined
      : data === undefined || (archiveNeeded && archivePages === undefined));

  // KAN-670: в «Все» подгружать надо оба источника — сначала живые турниры,
  // потом хвост архива. Если тянуть только живые, список замирает на первой же
  // архивной записи и выглядит законченным, хотя архив не дочитан.
  const hasMore = isArchiveView
    ? Boolean(hasNextArchivePage)
    : Boolean(hasNextPage) ||
      (statusFilter === "all" && Boolean(hasNextArchivePage));

  const loadMore = useMemo(() => {
    if (isArchiveView) {
      return hasNextArchivePage && !isFetchingNextArchivePage
        ? () => fetchNextArchivePage()
        : undefined;
    }
    if (hasNextPage && !isFetchingNextPage) return () => fetchNextPage();
    if (
      statusFilter === "all" &&
      hasNextArchivePage &&
      !isFetchingNextArchivePage
    ) {
      return () => fetchNextArchivePage();
    }
    return undefined;
  }, [
    isArchiveView,
    statusFilter,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    hasNextArchivePage,
    isFetchingNextArchivePage,
    fetchNextArchivePage,
  ]);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <div className="space-y-4 sm:space-y-6 rounded-2xl border border-border/40 bg-black/5 px-3 py-4 sm:px-4 sm:py-5 shadow-sm backdrop-blur-sm dark:bg-white/5 md:px-6">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl font-semibold tracking-tight truncate">
                {t("title")}
              </h1>
            </div>
            {canCreateTournament ? (
              <Button
                asChild
                className="transition-transform duration-150 hover:-translate-y-0.5"
              >
                <Link href="/create-tournament">{t("create")}</Link>
              </Button>
            ) : null}
          </div>
          <TournamentFilters
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            isSiteAdmin={isSiteAdmin}
            viewMode={viewMode}
            setViewMode={setViewMode}
            sortBy={sortBy}
            setSortBy={setSortBy}
            withFriends={withFriends}
            setWithFriends={setWithFriends}
            isAuthenticated={!!me}
          />
          <section
            aria-labelledby="tournaments-list-heading"
            className="rounded-xl border border-border/30 bg-background/60 p-2 sm:p-4 shadow-sm transition-colors"
          >
            {/* KAN-1007: карточки турниров — h3, а на странице был только h1.
                Заголовок секции закрывает пропущенный уровень, не меняя вид. */}
            <h2 id="tournaments-list-heading" className="sr-only">
              {t("listHeading")}
            </h2>
            {awaitingFirstData && serverFallback ? (
              serverFallback
            ) : (
            <TournamentListOptimized
              tournaments={visibleTournaments as Tournament[]}
              tournamentFriendsMap={tournamentFriendsMap}
              isLoading={showListSkeleton}
              viewMode={viewMode}
              filtered={
                Boolean(searchTerm?.trim()) ||
                statusFilter !== "all" ||
                withFriends ||
                isArchiveView
              }
              onLoadMore={loadMore}
              hasMore={hasMore}
            />
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
